import { useState, useEffect } from 'react';
import PermissionMatrix from '../PermissionMatrix/PermissionMatrix';
import { createRole, updateRole, getRoles } from '../../api/roleApi';
import './RoleForm.css';

export default function RoleForm({ role, onClose, onSaved }) {
  const isEdit = !!role;
  const isProtected = role?.is_protected;

  const [name, setName] = useState(role?.name || '');
  const [description, setDescription] = useState(role?.description || '');
  const [permissions, setPermissions] = useState(role?.permissions || {});
  const [cloneFrom, setCloneFrom] = useState('');
  const [allRoles, setAllRoles] = useState([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isEdit) {
      getRoles().then(setAllRoles).catch(() => {});
    }
  }, [isEdit]);

  const handleClone = (roleId) => {
    setCloneFrom(roleId);
    const source = allRoles.find(r => r._id === roleId);
    if (source) setPermissions({ ...source.permissions });
  };

  const handleSave = async () => {
    if (!name.trim()) { setError('Role name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        const payload = { permissions };
        if (!isProtected) { payload.name = name; payload.description = description; }
        await updateRole(role._id, payload);
      } else {
        await createRole({ name, description, permissions });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="role-form-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="role-form-modal">
        <div className="role-form-modal__header">
          <span className="role-form-modal__title">{isEdit ? `Edit Role \u2014 ${role.name}` : 'Create New Role'}</span>
          <button className="role-form-modal__close" onClick={onClose}>{'\u2715'}</button>
        </div>
        <div className="role-form-modal__body">
          {error && <div className="role-form-modal__error">{error}</div>}

          {!isEdit && (
            <div className="role-form-modal__field">
              <label className="role-form-modal__label">Clone Permissions From (optional)</label>
              <select className="role-form-modal__select" value={cloneFrom} onChange={e => handleClone(e.target.value)}>
                <option value="">\u2014 Start blank \u2014</option>
                {allRoles.map(r => <option key={r._id} value={r._id}>{r.name}</option>)}
              </select>
            </div>
          )}

          <div className="role-form-modal__row">
            <div className="role-form-modal__field">
              <label className="role-form-modal__label">Role Name *</label>
              <input
                className="role-form-modal__input"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="e.g. Compliance Officer"
                disabled={isProtected}
              />
              {isProtected && (
                <div className="role-form-modal__hint">Protected role \u2014 name cannot be changed.</div>
              )}
            </div>
            <div className="role-form-modal__field">
              <label className="role-form-modal__label">Description</label>
              <input
                className="role-form-modal__input"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Brief description of this role"
                disabled={isProtected}
              />
            </div>
          </div>

          <div className="role-form-modal__divider" />
          <div className="role-form-modal__section-label">Permission Matrix</div>
          <PermissionMatrix permissions={permissions} onChange={setPermissions} readOnly={false} />
        </div>
        <div className="role-form-modal__footer">
          <button className="btn btn--outline" onClick={onClose}>Cancel</button>
          <button className="btn btn--primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving\u2026' : isEdit ? 'Update Role' : 'Create Role'}
          </button>
        </div>
      </div>
    </div>
  );
}
